/**
 * «Состояние» (/absences — не /status: в dev этот путь занят сервером Metro) — кто отсутствует в выбранный день и почему (макет 29.10.2026).
 *
 * Источник — GET /users/absences: согласованные заявки ЭДО (после шага HR) — отпуск и его виды,
 * командировка, больничный, отгул; заменяющий — из заявки на отпуск. Причину и даты видят все
 * (решение владельца 29.10.2026).
 *
 * День — стрелками или календарём (поле DateField) от сегодня на 30 дней вперёд; прошлое не
 * показываем. Юрлица — чипами.
 * Плитки — общий MetricTile главной (четвёртая, «В отгуле», сверх макета) и заодно фильтр по
 * состоянию: нажатие оставляет в списке только этот вид, повторное — снимает. Широко — таблица,
 * узко (телефон) — карточки. Клик по сотруднику — его карточка (PersonDrawer).
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../src/api/client'
import { useApp, type Lang } from '../../src/i18n'
import { useAbsences } from '../../src/features/users/useUsers'
import { PersonDrawer, liveVM, type PersonVM } from '../../src/features/users/PersonDrawer'
import type { Absence, AbsenceKind } from '../../src/lib/types'
import { tone, type Tone } from '../../src/theme/tones'
import { Card, Chip, DateField, Enter, Icon, IconButton, Link, MetricTile, QueryState, SkeletonLines, Tag, type Metric } from '../../src/ui'
import { pressable } from '../../src/ui/primitives'
import { Page } from '../../src/ui/Screen'

const LOCALE: Record<Lang, string> = { ru: 'ru-RU', en: 'en-US', az: 'az-AZ', ka: 'ka-GE' }
const TABLE_MIN = 820 // уже — карточки вместо таблицы
const TILE_MIN = 150 // по две плитки в ряд на телефоне
/** Окно листания: от сегодня вперёд (планирование); прошлое не показываем (решение владельца
 *  29.10.2026: история отсутствий тут не нужна). Сервер держит ту же границу. */
const HORIZON_DAYS = 30

const KINDS: { kind: AbsenceKind; label: string; icon: string; tone: Tone }[] = [
  { kind: 'vacation', label: 'st.onVacation', icon: 'palmtree', tone: 'blue' },
  { kind: 'trip', label: 'st.onTrip', icon: 'plane', tone: 'gold' },
  { kind: 'sick', label: 'st.onSick', icon: 'thermometer', tone: 'red' },
  { kind: 'time_off', label: 'st.onTimeOff', icon: 'clock', tone: 'purple' },
]
const toneOf = (k: AbsenceKind): Tone => KINDS.find((x) => x.kind === k)?.tone ?? 'default'

/** Локальная дата YYYY-MM-DD (не UTC: листаем дни так, как их видит пользователь). */
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
/** YYYY-MM-DD → локальная полночь (без сдвига часового пояса, как у `new Date('YYYY-MM-DD')`). */
const parseYmd = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const shiftDay = (s: string, delta: number) => {
  const d = parseYmd(s)
  d.setDate(d.getDate() + delta)
  return ymd(d)
}

function usePeriodText() {
  const { l, lang } = useApp()
  const dm = (d: Date) => new Intl.DateTimeFormat(LOCALE[lang], { day: '2-digit', month: '2-digit' }).format(d)
  const hm = (d: Date) => new Intl.DateTimeFormat(LOCALE[lang], { hour: '2-digit', minute: '2-digit' }).format(d)
  return (a: Absence): string => {
    const { from, to } = a.period
    // Отгул — часы внутри дня: «12.11 10:00–13:00» в локальном поясе.
    if (a.kind === 'time_off') {
      const f = new Date(from)
      return to ? `${dm(f)} ${hm(f)}–${hm(new Date(to))}` : `${dm(f)} ${hm(f)}`
    }
    return to ? `${dm(parseYmd(from))} – ${dm(parseYmd(to))}` : `${dm(parseYmd(from))} – … (${l('st.openEnded')})`
  }
}

function PeriodCell({ a }: { a: Absence }) {
  return (
    <Text fontSize={13} color="$color">
      {usePeriodText()(a)}
    </Text>
  )
}

function KindTag({ kind }: { kind: AbsenceKind }) {
  const { l } = useApp()
  return <Tag tone={toneOf(kind)}>{l(`st.kind.${kind}`)}</Tag>
}

function PersonCell({ a, onPerson }: { a: Absence; onPerson: (id: string) => void }) {
  return (
    <YStack {...pressable} onPress={() => onPerson(a.user.id)} cursor="pointer" hoverStyle={{ opacity: 0.8 }} minWidth={0} gap={2}>
      <Text fontSize={13} fontWeight="700" color="$color" numberOfLines={1}>
        {a.user.fullName}
      </Text>
      {a.user.jobTitle ? (
        <Text fontSize={11} color="$muted" numberOfLines={1}>
          {a.user.jobTitle}
        </Text>
      ) : null}
    </YStack>
  )
}

// Таблица: доли колонок (сотрудник шире, состояние уже) — как в макете. flexBasis 0 обязателен:
// Tamagui даёт flex: N 1 auto — без него ширина колонки шла бы от содержимого и строки разъезжались.
const COLS = { person: 2.2, org: 1.6, state: 1.2, period: 1.4, deputy: 1.6 }

function HeadCell({ flex, children }: { flex: number; children: string }) {
  return (
    <Text flex={flex} flexBasis={0} minWidth={0} fontSize={11} fontWeight="700" color="$muted">
      {children}
    </Text>
  )
}

function AbsenceTable({ items, onPerson }: { items: Absence[]; onPerson: (id: string) => void }) {
  const { l } = useApp()
  return (
    <Card overflow="hidden">
      <XStack paddingHorizontal={16} paddingVertical={11} gap={16} borderBottomWidth={1} borderColor="$borderSecondary">
        <HeadCell flex={COLS.person}>{l('st.employee')}</HeadCell>
        <HeadCell flex={COLS.org}>{l('st.org')}</HeadCell>
        <HeadCell flex={COLS.state}>{l('st.state')}</HeadCell>
        <HeadCell flex={COLS.period}>{l('st.period')}</HeadCell>
        <HeadCell flex={COLS.deputy}>{l('st.deputy')}</HeadCell>
      </XStack>
      {items.map((a, i) => (
        <XStack
          key={a.id}
          paddingHorizontal={16}
          paddingVertical={12}
          gap={16}
          alignItems="center"
          {...(i ? { borderTopWidth: 1, borderColor: '$borderSecondary' } : null)}
        >
          <YStack flex={COLS.person} flexBasis={0} minWidth={0}>
            <PersonCell a={a} onPerson={onPerson} />
          </YStack>
          <Text flex={COLS.org} flexBasis={0} minWidth={0} fontSize={13} color="$color" numberOfLines={1}>
            {a.company?.name ?? '—'}
          </Text>
          <YStack flex={COLS.state} flexBasis={0} minWidth={0}>
            <KindTag kind={a.kind} />
          </YStack>
          <YStack flex={COLS.period} flexBasis={0} minWidth={0}>
            <PeriodCell a={a} />
          </YStack>
          <Text flex={COLS.deputy} flexBasis={0} minWidth={0} fontSize={13} color="$color" numberOfLines={1}>
            {a.deputy?.fullName ?? '—'}
          </Text>
        </XStack>
      ))}
    </Card>
  )
}

function AbsenceCards({ items, onPerson }: { items: Absence[]; onPerson: (id: string) => void }) {
  const { l } = useApp()
  return (
    <Card overflow="hidden">
      {items.map((a, i) => (
        <YStack key={a.id} padding={14} gap={8} {...(i ? { borderTopWidth: 1, borderColor: '$borderSecondary' } : null)}>
          <XStack alignItems="flex-start" justifyContent="space-between" gap={10}>
            <YStack flex={1} minWidth={0}>
              <PersonCell a={a} onPerson={onPerson} />
            </YStack>
            <KindTag kind={a.kind} />
          </XStack>
          <XStack alignItems="center" columnGap={8} rowGap={4} flexWrap="wrap">
            <PeriodCell a={a} />
            {a.company ? (
              <Text fontSize={12} color="$muted">
                · {a.company.name}
              </Text>
            ) : null}
          </XStack>
          {a.deputy ? (
            <Text fontSize={12} color="$muted">
              {l('st.deputy')}: <Text color="$color">{a.deputy.fullName}</Text>
            </Text>
          ) : null}
        </YStack>
      ))}
    </Card>
  )
}

export default function StatusScreen() {
  const { l, lang } = useApp()
  const qc = useQueryClient()
  const today = ymd(new Date())
  const maxDate = shiftDay(today, HORIZON_DAYS)
  const [date, setDate] = useState(today)
  const canPrev = date > today
  const canNext = date < maxDate
  const [org, setOrg] = useState('all')
  // Плитка-фильтр по состоянию: нажать — только этот вид, ещё раз — снять (29.10.2026).
  const [kind, setKind] = useState<AbsenceKind | null>(null)
  const [sel, setSel] = useState<PersonVM | null>(null)
  const [w, setW] = useState<number | null>(null)
  const q = useAbsences(date)
  const narrow = (w ?? TABLE_MIN) < TABLE_MIN
  const items = q.data?.items ?? []

  // Юрлица — из ответа дня; выбранное держим в ряду, даже если сегодня по нему пусто.
  const companies = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of items) if (a.company) m.set(a.company.id, a.company.name)
    return [...m].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [items])
  // Числа на плитках — по юрлицу, без фильтра по виду: видно, сколько всего каждого состояния.
  const byOrg = org === 'all' ? items : items.filter((a) => a.company?.id === org)
  const shown = kind ? byOrg.filter((a) => a.kind === kind) : byOrg

  const metrics: Metric[] = KINDS.map((k) => ({
    key: k.kind,
    label: k.label,
    value: byOrg.filter((a) => a.kind === k.kind).length,
    icon: k.icon,
    color: tone(k.tone),
    loading: q.isLoading,
  }))

  const dayLabel =
    date === today
      ? `${l('st.today')}, ${new Intl.DateTimeFormat(LOCALE[lang], { day: '2-digit', month: '2-digit' }).format(parseYmd(date))}`
      : cap(new Intl.DateTimeFormat(LOCALE[lang], { weekday: 'short', day: '2-digit', month: '2-digit' }).format(parseYmd(date)))

  const openUser = (userId: string) => {
    qc.fetchQuery({ queryKey: ['user', userId], queryFn: () => api.user(userId) })
      .then((profile) => setSel(liveVM(profile)))
      .catch(() => undefined)
  }

  const dayPicker = (
    <XStack alignItems="center" gap={6} flexWrap="wrap" rowGap={4}>
      <IconButton disabled={!canPrev} opacity={canPrev ? 1 : 0.35} onPress={() => setDate((d) => (d > today ? shiftDay(d, -1) : d))} aria-label={l('st.prevDay')}>
        <Icon name="chevron-left" size={18} color="$color" />
      </IconButton>
      {/* дата — поле с календарём на месяц вперёд (общий DateField форм; дни вне окна неактивны),
          рядом стрелки на день назад/вперёд */}
      <Stack width={158}>
        <DateField value={date} onChange={setDate} min={today} max={maxDate} display={dayLabel} align={narrow ? 'left' : 'right'} />
      </Stack>
      <IconButton disabled={!canNext} opacity={canNext ? 1 : 0.35} onPress={() => setDate((d) => (d < maxDate ? shiftDay(d, 1) : d))} aria-label={l('st.nextDay')}>
        <Icon name="chevron-right" size={18} color="$color" />
      </IconButton>
      {date !== today ? (
        <Link fontSize={12} marginLeft={4} onPress={() => setDate(today)}>
          {l('st.today')}
        </Link>
      ) : null}
    </XStack>
  )

  return (
    <>
      <Page title={l('st.title')} action={dayPicker}>
        <YStack
          gap={16}
          onLayout={(e) => {
            const v = Math.round(e.nativeEvent.layout.width)
            setW((prev) => (prev === v ? prev : v))
          }}
        >
          <XStack alignItems="center" gap={8} flexWrap="wrap">
            <Text fontSize={12} color="$muted" marginRight={2}>
              {l('st.org')}:
            </Text>
            <Chip on={org === 'all'} dim onPress={() => setOrg('all')}>
              {l('st.all')}
            </Chip>
            {companies.map((c) => (
              <Chip key={c.id} on={org === c.id} dim onPress={() => setOrg(c.id)}>
                {c.name}
              </Chip>
            ))}
          </XStack>

          <XStack flexWrap="wrap" gap={16}>
            {/* четыре плитки: на широком — в ряд, на телефоне — 2×2; flexBasis 0 — ширина поровну, а не по длине подписи */}
            {metrics.map((m, i) => (
              <Enter key={m.key} index={i} flex={1} flexBasis={0} minWidth={TILE_MIN}>
                <MetricTile
                  m={m}
                  minWidth={TILE_MIN}
                  compact={narrow}
                  active={kind === m.key}
                  onPress={() => setKind((cur) => (cur === m.key ? null : (m.key as AbsenceKind)))}
                />
              </Enter>
            ))}
          </XStack>

          <QueryState q={q}>
            {q.isLoading ? (
              <Card padding={20}>
                <SkeletonLines lines={5} />
              </Card>
            ) : shown.length === 0 ? (
              <Card padding={40} alignItems="center" gap={10}>
                <Icon name="user-check" size={26} color="$tertiary" />
                <Text fontSize={13} color="$muted">
                  {l(kind ? 'st.emptyKind' : 'st.empty')}
                </Text>
              </Card>
            ) : !narrow ? (
              <AbsenceTable items={shown} onPerson={openUser} />
            ) : (
              <AbsenceCards items={shown} onPerson={openUser} />
            )}
          </QueryState>

        </YStack>
        <Stack height={8} />
      </Page>

      <PersonDrawer p={sel} onClose={() => setSel(null)} />
    </>
  )
}
